import React, { useCallback, useEffect, useRef, useState } from 'react';

// ── Pannable / zoomable document preview ─────────────────────────
function DocPreview({ srcDoc, docW = 820, docH = 1100 }) {
  const [zoom, setZoom] = React.useState(0.56);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const dragging = React.useRef(false);
  const last = React.useRef({ x: 0, y: 0 });
  const containerRef = React.useRef(null);

  const clampPan = React.useCallback((x, y, z) => {
    const cw = containerRef.current?.clientWidth || 600;
    const ch = containerRef.current?.clientHeight || 520;
    const maxX = Math.max(0, docW * z - cw);
    const maxY = Math.max(0, docH * z - ch);
    return { x: Math.min(0, Math.max(-maxX, x)), y: Math.min(0, Math.max(-maxY, y)) };
  }, [docW, docH]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoom(z => {
      const nz = Math.min(2, Math.max(0.25, +(z + delta).toFixed(2)));
      setPan(p => clampPan(p.x, p.y, nz));
      return nz;
    });
  };

  const onMouseDown = (e) => {
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setPan(p => clampPan(p.x + dx, p.y + dy, zoom));
  };
  const onMouseUp = (e) => { dragging.current = false; e.currentTarget.style.cursor = 'grab'; };

  const resetView = () => { setZoom(0.56); setPan({ x: 0, y: 0 }); };
  const zoomIn = () => setZoom(z => { const nz = Math.min(2, +(z + 0.15).toFixed(2)); setPan(p => clampPan(p.x, p.y, nz)); return nz; });
  const zoomOut = () => setZoom(z => { const nz = Math.max(0.25, +(z - 0.15).toFixed(2)); setPan(p => clampPan(p.x, p.y, nz)); return nz; });

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 px-1">
        <button onClick={zoomOut}
          className="w-7 h-7 rounded-lg bg-surface-container flex items-center justify-center text-on-surface-variant hover:bg-surface-variant border-0 cursor-pointer text-lg font-bold leading-none transition-colors select-none">−</button>
        <span className="text-[11px] font-semibold text-on-surface-variant w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
        <button onClick={zoomIn}
          className="w-7 h-7 rounded-lg bg-surface-container flex items-center justify-center text-on-surface-variant hover:bg-surface-variant border-0 cursor-pointer text-lg font-bold leading-none transition-colors select-none">+</button>
        <div className="w-px h-4 bg-surface-variant mx-1" />
        <button onClick={resetView}
          className="px-2.5 h-7 rounded-lg bg-surface-container text-[10px] font-bold text-on-surface-variant hover:bg-surface-variant border-0 cursor-pointer transition-colors uppercase tracking-wide select-none">Fit</button>
        <span className="ml-auto text-[10px] text-on-surface-variant/50 italic">Scroll to zoom · Drag to pan</span>
      </div>
      {/* Canvas */}
      <div
        ref={containerRef}
        className="relative w-full rounded-xl bg-surface-container-lowest overflow-hidden"
        style={{ height: '540px', cursor: 'grab', userSelect: 'none' }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Checkerboard bg hint */}
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle,#94a3b8 1px,transparent 1px)', backgroundSize: '20px 20px' }} />
        <div style={{ position: 'absolute', left: pan.x, top: pan.y, width: docW * zoom, height: docH * zoom, willChange: 'transform' }}>
          <iframe
            srcDoc={srcDoc}
            style={{ width: docW + 'px', height: docH + 'px', border: 'none', transformOrigin: 'top left', transform: `scale(${zoom})`, display: 'block', pointerEvents: 'none' }}
            title="Document Preview"
          />
        </div>
      </div>
    </div>
  );
}


export default DocPreview;
